/**
 * Materials Drawer — data utilities.
 *
 * Pure helpers extracted from `MaterialsDrawer.jsx` so the parent component
 * stays a thin UI shell. These functions handle:
 *   - the empty default shape used when no BOM exists yet
 *   - normalising backend payloads → form-friendly state (strings, _uids)
 *   - serialising form state → backend payload (numbers, nulls)
 */

export const KARATS = ["22kt", "18kt", "14kt"];
export const GOLD_COLORS = ["yellow", "white", "rose"];

export const EMPTY_MATERIALS = {
  gold: { weight_grams: "", karat: "22kt", color: "yellow", notes: "" },
  diamonds: { total_carat: "", count: "", cut: "", quality: "", notes: "" },
  gemstones: [],
  findings: [],
  price_range: { currency: "INR", low: "", high: "", notes: "" },
  summary: "",
};

const num = (v) => (v === null || v === undefined || Number.isNaN(v) ? "" : String(v));
const newUid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Coerce a backend BOM payload into a form-friendly shape:
 * - numeric|null → "" string
 * - every gemstone / finding gets a stable `_uid` for React keys
 */
export function normalize(m) {
  if (!m) return EMPTY_MATERIALS;
  return {
    gold: {
      weight_grams: num(m.gold?.weight_grams),
      karat: m.gold?.karat || "22kt",
      color: m.gold?.color || "yellow",
      notes: m.gold?.notes || "",
    },
    diamonds: {
      total_carat: num(m.diamonds?.total_carat),
      count: num(m.diamonds?.count),
      cut: m.diamonds?.cut || "",
      quality: m.diamonds?.quality || "",
      notes: m.diamonds?.notes || "",
    },
    gemstones: (m.gemstones || []).map((g) => ({
      _uid: g._uid || newUid("g"),
      name: g.name || "",
      cut: g.cut || "",
      count: num(g.count),
      total_carat: num(g.total_carat),
      notes: g.notes || "",
    })),
    findings: (m.findings || []).map((f) => ({
      _uid: f._uid || newUid("f"),
      name: f.name || "",
      count: num(f.count),
      notes: f.notes || "",
    })),
    price_range: {
      currency: m.price_range?.currency || "INR",
      low: num(m.price_range?.low),
      high: num(m.price_range?.high),
      notes: m.price_range?.notes || "",
    },
    summary: m.summary || "",
  };
}

/**
 * Convert form state → backend payload:
 * - strings → numbers (float / int) or null
 * - empty strings → null
 * - filters out unnamed gemstones / findings
 * - drops client-side `_uid`s
 */
export function serialize(m) {
  const n = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const f = Number(v);
    return Number.isFinite(f) ? f : null;
  };
  const i = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const x = parseInt(v, 10);
    return Number.isFinite(x) ? x : null;
  };
  const strOrNull = (v) => (v && String(v).trim() ? String(v).trim() : null);

  return {
    gold: {
      weight_grams: n(m.gold.weight_grams),
      karat: strOrNull(m.gold.karat),
      color: strOrNull(m.gold.color),
      notes: strOrNull(m.gold.notes),
    },
    diamonds: {
      total_carat: n(m.diamonds.total_carat),
      count: i(m.diamonds.count),
      cut: strOrNull(m.diamonds.cut),
      quality: strOrNull(m.diamonds.quality),
      notes: strOrNull(m.diamonds.notes),
    },
    gemstones: m.gemstones
      .filter((g) => (g.name || "").trim())
      .map((g) => ({
        name: g.name.trim(),
        cut: strOrNull(g.cut),
        count: i(g.count),
        total_carat: n(g.total_carat),
        notes: strOrNull(g.notes),
      })),
    findings: m.findings
      .filter((f) => (f.name || "").trim())
      .map((f) => ({
        name: f.name.trim(),
        count: i(f.count),
        notes: strOrNull(f.notes),
      })),
    price_range: {
      currency: m.price_range.currency || "INR",
      low: n(m.price_range.low),
      high: n(m.price_range.high),
      notes: strOrNull(m.price_range.notes),
    },
    summary: strOrNull(m.summary),
  };
}

/** Generates a stable `_uid` for newly-added gemstone or finding rows. */
export function freshUid(group) {
  return newUid(group === "gemstones" ? "g" : "f");
}
